import React, { useContext, useEffect, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Navbar,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import AdminService from "../../../services/admin.service";
import { LoaderContext } from "../../../Context";

const dropdownFields = [
  "gender",
  "centerPreference",
  "allocatedCenterId",
  "city",
  "state",
  "zone",
  "vendorName",
];

const userFields: {
  key: string;
  label: string;
  required?: boolean;
  type?: string;
}[] = [
  { key: "rollNo", label: "Roll No", required: true },
  { key: "regNo", label: "Registration No" },
  { key: "name", label: "Name" },
  { key: "fatherName", label: "Father Name" },
  { key: "gender", label: "Gender" },
  { key: "dob", label: "Date of Birth", type: "date" },
  { key: "email", label: "Email", type: "email" },
  { key: "mobileNo", label: "Mobile No" },
  { key: "aadharNo", label: "Aadhar No" },
  { key: "centerPreference", label: "Center Preference" },
  { key: "allocatedCenterId", label: "Allocated Center ID" },
  { key: "centerName", label: "Center Name" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zone", label: "Zone" },
  { key: "category", label: "Category" },
  { key: "courseName", label: "Course Name" },
  { key: "vendorName", label: "Vendor Name" },
  { key: "address", label: "Address" },
  { key: "dateOfExam", label: "Date of Exam", type: "date" },
];

const vendorOptions = [
  { value: "BRIGHT", label: "BRIGHT" },
  { value: "SAI", label: "SAI" },
];

const deviceOptions = [
  { value: "MFS100", label: "MFS100" },
  { value: "STARTEK.ACPL", label: "STARTEK.ACPL" },
];

const toOptions = (arr: string[]) =>
  arr.map((v) => ({ value: v, label: v }));

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });
};

const UserLookup = () => {
  const { setLoading }: any = useContext(LoaderContext);

  const [exams, setExams] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [selectedExam, setSelectedExam] = useState<any>(null);
  const [selectedShift, setSelectedShift] = useState<any>(null);

  const [searchType, setSearchType] = useState<string>("rollNo");
  const [searchValue, setSearchValue] = useState<string>("");
  const [searching, setSearching] = useState<boolean>(false);

  const [searchDone, setSearchDone] = useState<boolean>(false);
  const [foundUser, setFoundUser] = useState<any>(null);

  const [formData, setFormData] = useState<any>({});
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [creating, setCreating] = useState<boolean>(false);

  const [dropdownOptions, setDropdownOptions] = useState<
    Record<string, any[]>
  >({});

  // Verification state
  const [verifications, setVerifications] = useState<any[]>([]);
  const [loadingVerifications, setLoadingVerifications] = useState(false);
  const [showCreateVerification, setShowCreateVerification] = useState(false);
  const [vfForm, setVfForm] = useState<any>({
    centerId: "",
    vendorName: "",
    device: "",
    data: "",
  });
  const [vfUserLivePhoto, setVfUserLivePhoto] = useState<File | null>(null);
  const [vfImage, setVfImage] = useState<File | null>(null);
  const [creatingVerification, setCreatingVerification] = useState(false);

  useEffect(() => {
    fetchExams();
  }, []);

  useEffect(() => {
    if (selectedExam) {
      fetchShifts(selectedExam.value);
      setSelectedShift(null);
      resetSearch();
      setDropdownOptions({});
    }
  }, [selectedExam]);

  useEffect(() => {
    resetSearch();
    if (selectedExam && selectedShift) {
      fetchDropdownOptions();
    }
  }, [selectedShift]);

  useEffect(() => {
    if (foundUser?._id && selectedExam && selectedShift) {
      fetchVerifications(foundUser._id);
    } else {
      setVerifications([]);
    }
  }, [foundUser]);

  const fetchExams = async () => {
    try {
      const res = await AdminService.getAllExamName();
      if (res.status === 200) {
        setExams(
          res.data.data.examName.map((d: any) => ({
            value: d._id,
            label: d.name,
          }))
        );
      }
    } catch (err) {
      toast.error("Failed to load exams");
    }
  };

  const fetchShifts = async (examId: string) => {
    try {
      const res = await AdminService.getShiftData(examId);
      if (res.status === 200) {
        setShifts(
          res.data.data.shifts.map((d: any) => ({
            value: d._id,
            label: d.shiftName,
          }))
        );
      }
    } catch (err) {
      toast.error("Failed to load shifts");
    }
  };

  const fetchDropdownOptions = async () => {
    if (!selectedExam || !selectedShift) return;
    try {
      const res = await AdminService.getUserDropdownOptions(
        selectedExam.value,
        selectedShift.value
      );
      if (res.status === 200 && res.data?.data) {
        setDropdownOptions(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load dropdown options", err);
    }
  };

  const fetchVerifications = async (studentId: string) => {
    setLoadingVerifications(true);
    try {
      const res = await AdminService.getUserVerifications(
        selectedExam.value,
        selectedShift.value,
        studentId
      );
      if (res.status === 200 && res.data?.data?.verifications) {
        setVerifications(res.data.data.verifications);
      } else {
        setVerifications([]);
      }
    } catch (err) {
      console.error("Failed to load verifications", err);
      setVerifications([]);
    } finally {
      setLoadingVerifications(false);
    }
  };

  const resetSearch = () => {
    setSearchDone(false);
    setFoundUser(null);
    setFormData({});
    setAvatarFile(null);
    setSearchValue("");
    setVerifications([]);
    setShowCreateVerification(false);
  };

  const handleSearch = async () => {
    if (!selectedExam || !selectedShift) {
      toast.error("Please select exam and shift first");
      return;
    }
    if (!searchValue.trim()) {
      toast.error("Please enter a search value");
      return;
    }

    setSearching(true);
    setSearchDone(false);
    setFoundUser(null);
    setVerifications([]);

    try {
      const rollNo =
        searchType === "rollNo" ? searchValue.trim() : undefined;
      const regNo =
        searchType === "regNo" ? searchValue.trim() : undefined;
      const res = await AdminService.searchUser(
        selectedExam.value,
        selectedShift.value,
        rollNo,
        regNo
      );
      if (res.data?.data?.found) {
        setFoundUser(res.data.data.user);
      } else {
        setFoundUser(null);
        setFormData({ [searchType]: searchValue.trim() });
      }
      setSearchDone(true);
    } catch (err) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleFormChange = (key: string, value: string) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleCreateUser = async () => {
    if (!formData.rollNo) {
      toast.error("Roll No is required");
      return;
    }
    if (!avatarFile) {
      toast.error("Please upload student photo");
      return;
    }

    setCreating(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key]) fd.append(key, formData[key]);
      });
      fd.append("file", avatarFile);

      const res = await AdminService.createStudent(
        fd,
        selectedExam.value,
        selectedShift.value
      );
      if (res.status === 200) {
        toast.success("User created successfully");
        setFoundUser(res.data);
        setFormData({});
        setAvatarFile(null);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          err?.response?.data ||
          "Failed to create user"
      );
    } finally {
      setCreating(false);
    }
  };

  const handleCreateVerification = async () => {
    if (!vfForm.centerId) {
      toast.error("Center ID is required");
      return;
    }

    setCreatingVerification(true);
    try {
      let userLivePhotoBase64 = "";
      let imageBase64 = "";

      if (vfUserLivePhoto) {
        userLivePhotoBase64 = await fileToBase64(vfUserLivePhoto);
      }
      if (vfImage) {
        imageBase64 = await fileToBase64(vfImage);
      }

      const payload = {
        studentId: foundUser._id,
        centerId: vfForm.centerId,
        vendorName: vfForm.vendorName,
        device: vfForm.device,
        method: "fingerprint",
        data: vfForm.data,
        userLivePhoto: userLivePhotoBase64,
        image: imageBase64,
      };

      const res = await AdminService.createVerification(
        selectedExam.value,
        selectedShift.value,
        payload
      );

      if (res.status === 200) {
        toast.success("Verification created successfully");
        setShowCreateVerification(false);
        setVfForm({ centerId: "", vendorName: "", device: "", data: "" });
        setVfUserLivePhoto(null);
        setVfImage(null);
        fetchVerifications(foundUser._id);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to create verification"
      );
    } finally {
      setCreatingVerification(false);
    }
  };

  const formatValue = (key: string, val: any) => {
    if (!val) return "-";
    if (
      key === "dob" ||
      key === "dateOfExam" ||
      key === "createdAt" ||
      key === "updatedAt"
    ) {
      return new Date(val).toLocaleDateString();
    }
    if (key === "avatar") {
      return (
        <a href={val} target="_blank" rel="noreferrer">
          View Photo
        </a>
      );
    }
    return String(val);
  };

  const isDropdownField = (key: string) => dropdownFields.includes(key);

  const renderFormField = (field: {
    key: string;
    label: string;
    required?: boolean;
    type?: string;
  }) => {
    if (isDropdownField(field.key)) {
      const options = toOptions(dropdownOptions[field.key] || []);
      const selectedOption = formData[field.key]
        ? { value: formData[field.key], label: formData[field.key] }
        : null;
      return (
        <Col md={4} key={field.key} className="mb-3">
          <Form.Group>
            <Form.Label className="xcn-input-label text-secondary">
              {field.label}
              {field.required && <span className="text-danger">*</span>}
            </Form.Label>
            <Select
              options={options}
              value={selectedOption}
              onChange={(opt: any) =>
                handleFormChange(field.key, opt?.value || "")
              }
              isClearable
              placeholder={`Select ${field.label}`}
            />
          </Form.Group>
        </Col>
      );
    }

    return (
      <Col md={4} key={field.key} className="mb-3">
        <Form.Group>
          <Form.Label className="xcn-input-label text-secondary">
            {field.label}
            {field.required && <span className="text-danger">*</span>}
          </Form.Label>
          <Form.Control
            type={field.type || "text"}
            value={formData[field.key] || ""}
            onChange={(e) => handleFormChange(field.key, e.target.value)}
          />
        </Form.Group>
      </Col>
    );
  };

  const centerOptions = toOptions(dropdownOptions["centerPreference"] || []);

  return (
    <>
      <Navbar bg="light" expand="lg" className="navbarWrapper">
        <Container fluid>
          <Navbar.Brand>User Lookup</Navbar.Brand>
          <div className="d-flex gap-3 align-items-center">
            <Select
              options={exams}
              onChange={setSelectedExam}
              value={selectedExam}
              placeholder="Select Exam"
              styles={{
                container: (base: any) => ({ ...base, minWidth: 200 }),
              }}
            />
            <Select
              options={shifts}
              onChange={setSelectedShift}
              value={selectedShift}
              placeholder="Select Shift"
              isDisabled={!selectedExam}
              styles={{
                container: (base: any) => ({ ...base, minWidth: 200 }),
              }}
            />
          </div>
        </Container>
      </Navbar>

      {selectedExam && selectedShift && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-primary mb-3">Search Candidate</h6>
            <div className="d-flex gap-3 align-items-end">
              <Form.Group>
                <Form.Label className="xcn-input-label text-secondary">
                  Search By
                </Form.Label>
                <Form.Select
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setSearchValue("");
                    setSearchDone(false);
                    setFoundUser(null);
                  }}
                >
                  <option value="rollNo">Roll No</option>
                  <option value="regNo">Registration No</option>
                </Form.Select>
              </Form.Group>
              <Form.Group style={{ minWidth: 250 }}>
                <Form.Label className="xcn-input-label text-secondary">
                  {searchType === "rollNo" ? "Roll No" : "Registration No"}
                </Form.Label>
                <Form.Control
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={`Enter ${
                    searchType === "rollNo" ? "Roll No" : "Reg No"
                  }`}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
              </Form.Group>
              <Button onClick={handleSearch} disabled={searching}>
                {searching ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Search"
                )}
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* User Details */}
      {searchDone && foundUser && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-success mb-3">User Found</h6>
            <Table bordered hover size="sm">
              <tbody>
                {userFields.map((field) => (
                  <tr key={field.key}>
                    <td
                      className="fw-bold text-secondary"
                      style={{ width: "30%" }}
                    >
                      {field.label}
                    </td>
                    <td>{formatValue(field.key, foundUser[field.key])}</td>
                  </tr>
                ))}
                <tr>
                  <td className="fw-bold text-secondary">Avatar</td>
                  <td>{formatValue("avatar", foundUser.avatar)}</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Verifications Section */}
      {searchDone && foundUser && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-primary mb-0">
                Verifications{" "}
                <Badge bg="secondary">{verifications.length}</Badge>
              </h6>
              <Button
                size="sm"
                variant={showCreateVerification ? "outline-secondary" : "primary"}
                onClick={() =>
                  setShowCreateVerification(!showCreateVerification)
                }
              >
                {showCreateVerification ? "Cancel" : "Create Verification"}
              </Button>
            </div>

            {loadingVerifications && (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" /> Loading...
              </div>
            )}

            {!loadingVerifications && verifications.length === 0 && !showCreateVerification && (
              <p className="text-muted">
                No verifications found for this candidate.
              </p>
            )}

            {!loadingVerifications && verifications.length > 0 && (
              <Accordion>
                {verifications.map((vf: any, idx: number) => (
                  <Accordion.Item eventKey={String(idx)} key={vf._id}>
                    <Accordion.Header>
                      <div className="d-flex gap-3 align-items-center w-100">
                        <Badge bg="info">{vf.verificationId}</Badge>
                        <span className="text-muted small">
                          Center: {vf.centerId || "-"}
                        </span>
                        {vf.vendorName && (
                          <Badge bg="secondary">{vf.vendorName}</Badge>
                        )}
                        <span className="text-muted small">
                          {new Date(vf.createdAt).toLocaleString()}
                        </span>
                        {vf.verificationDetails?.[0]?.success && (
                          <Badge bg="success">Success</Badge>
                        )}
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <Table bordered size="sm">
                        <tbody>
                          <tr>
                            <td className="fw-bold text-secondary" style={{ width: "30%" }}>
                              Verification ID
                            </td>
                            <td>{vf.verificationId}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-secondary">
                              Center ID
                            </td>
                            <td>{vf.centerId || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-secondary">
                              Vendor Name
                            </td>
                            <td>{vf.vendorName || "-"}</td>
                          </tr>
                          <tr>
                            <td className="fw-bold text-secondary">
                              User Live Photo
                            </td>
                            <td>
                              {vf.userLivePhoto ? (
                                vf.userLivePhoto.startsWith("data:") ? (
                                  <img
                                    src={vf.userLivePhoto}
                                    alt="live"
                                    style={{ maxHeight: 80 }}
                                  />
                                ) : (
                                  <a
                                    href={vf.userLivePhoto}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    View
                                  </a>
                                )
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          {vf.invigilatorId && (
                            <tr>
                              <td className="fw-bold text-secondary">
                                Invigilator
                              </td>
                              <td>
                                {vf.invigilatorId.operatorName ||
                                  vf.invigilatorId.userName ||
                                  "-"}{" "}
                                ({vf.invigilatorId.operatorId ||
                                  vf.invigilatorId.emailId ||
                                  ""})
                              </td>
                            </tr>
                          )}
                          <tr>
                            <td className="fw-bold text-secondary">
                              Created At
                            </td>
                            <td>
                              {new Date(vf.createdAt).toLocaleString()}
                            </td>
                          </tr>
                        </tbody>
                      </Table>

                      {vf.verificationDetails &&
                        vf.verificationDetails.length > 0 && (
                          <>
                            <h6 className="mt-3 text-secondary">
                              Verification Details
                            </h6>
                            {vf.verificationDetails.map(
                              (detail: any, dIdx: number) => (
                                <Card
                                  key={dIdx}
                                  className="mb-2 border"
                                >
                                  <Card.Body className="py-2 px-3">
                                    <Table
                                      bordered
                                      size="sm"
                                      className="mb-0"
                                    >
                                      <tbody>
                                        <tr>
                                          <td className="fw-bold text-secondary" style={{ width: "30%" }}>
                                            Method
                                          </td>
                                          <td>{detail.method || "-"}</td>
                                        </tr>
                                        <tr>
                                          <td className="fw-bold text-secondary">
                                            Success
                                          </td>
                                          <td>
                                            <Badge
                                              bg={
                                                detail.success
                                                  ? "success"
                                                  : "danger"
                                              }
                                            >
                                              {detail.success
                                                ? "true"
                                                : "false"}
                                            </Badge>
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className="fw-bold text-secondary">
                                            Manually Verified
                                          </td>
                                          <td>
                                            {String(
                                              detail.manuallyVerified ?? false
                                            )}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className="fw-bold text-secondary">
                                            Device
                                          </td>
                                          <td>{detail.device || "-"}</td>
                                        </tr>
                                        <tr>
                                          <td className="fw-bold text-secondary">
                                            Image
                                          </td>
                                          <td>
                                            {detail.image ? (
                                              typeof detail.image ===
                                                "string" &&
                                              detail.image.startsWith(
                                                "data:"
                                              ) ? (
                                                <img
                                                  src={detail.image}
                                                  alt="fp"
                                                  style={{ maxHeight: 80 }}
                                                />
                                              ) : (
                                                "Present"
                                              )
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                        </tr>
                                        <tr>
                                          <td className="fw-bold text-secondary">
                                            Success Time
                                          </td>
                                          <td>
                                            {detail.success_time
                                              ? new Date(
                                                  detail.success_time
                                                ).toLocaleString()
                                              : "-"}
                                          </td>
                                        </tr>
                                        {detail.bypassReason && (
                                          <tr>
                                            <td className="fw-bold text-secondary">
                                              Bypass Reason
                                            </td>
                                            <td>{detail.bypassReason}</td>
                                          </tr>
                                        )}
                                        {detail.serialNumber && (
                                          <tr>
                                            <td className="fw-bold text-secondary">
                                              Serial Number
                                            </td>
                                            <td>{detail.serialNumber}</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </Table>
                                  </Card.Body>
                                </Card>
                              )
                            )}
                          </>
                        )}
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}

            {/* Create Verification Form */}
            {showCreateVerification && (
              <Card className="mt-3 border">
                <Card.Body>
                  <h6 className="text-warning mb-3">
                    Create New Verification
                  </h6>
                  <Row>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Center ID <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          options={centerOptions}
                          value={
                            vfForm.centerId
                              ? {
                                  value: vfForm.centerId,
                                  label: vfForm.centerId,
                                }
                              : null
                          }
                          onChange={(opt: any) =>
                            setVfForm((prev: any) => ({
                              ...prev,
                              centerId: opt?.value || "",
                            }))
                          }
                          isClearable
                          placeholder="Select Center ID"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Method
                        </Form.Label>
                        <Form.Control
                          value="fingerprint"
                          readOnly
                          disabled
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Vendor Name
                        </Form.Label>
                        <Select
                          options={vendorOptions}
                          value={
                            vfForm.vendorName
                              ? {
                                  value: vfForm.vendorName,
                                  label: vfForm.vendorName,
                                }
                              : null
                          }
                          onChange={(opt: any) =>
                            setVfForm((prev: any) => ({
                              ...prev,
                              vendorName: opt?.value || "",
                            }))
                          }
                          isClearable
                          placeholder="Select Vendor"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Device
                        </Form.Label>
                        <Select
                          options={deviceOptions}
                          value={
                            vfForm.device
                              ? {
                                  value: vfForm.device,
                                  label: vfForm.device,
                                }
                              : null
                          }
                          onChange={(opt: any) =>
                            setVfForm((prev: any) => ({
                              ...prev,
                              device: opt?.value || "",
                            }))
                          }
                          isClearable
                          placeholder="Select Device"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          User Live Photo
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e: any) =>
                            setVfUserLivePhoto(e.target.files?.[0] || null)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Verification Image (fingerprint)
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e: any) =>
                            setVfImage(e.target.files?.[0] || null)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12} className="mb-3">
                      <Form.Group>
                        <Form.Label className="xcn-input-label text-secondary">
                          Verification Data
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={vfForm.data}
                          onChange={(e) =>
                            setVfForm((prev: any) => ({
                              ...prev,
                              data: e.target.value,
                            }))
                          }
                          placeholder="Enter verification data"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Button
                    onClick={handleCreateVerification}
                    disabled={creatingVerification}
                  >
                    {creatingVerification ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      "Create Verification"
                    )}
                  </Button>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Create User Form */}
      {searchDone && !foundUser && (
        <Card className="shadow-sm p-3 mt-3 bg-white rounded mx-3">
          <Card.Body>
            <h6 className="text-warning mb-3">
              User not found. Create new user:
            </h6>
            <Row>
              {userFields.map((field) => renderFormField(field))}
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label className="xcn-input-label text-secondary">
                    Student Photo <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e: any) =>
                      setAvatarFile(e.target.files?.[0] || null)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Button
              onClick={handleCreateUser}
              disabled={creating}
              className="mt-2"
            >
              {creating ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Create User"
              )}
            </Button>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default UserLookup;
